/**
 * Phase 4 API wiring — callables, the Ask Atlas SSE client, and the Firestore
 * subscriptions for annotations / presence / boards.
 *
 * This module is the thin Firebase SDK layer; the presentational views and
 * their pure helpers carry the logic and the tests. Coverage-excluded — it is
 * exercised by the emulator, not unit tests.
 */
/* c8 ignore start */
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  collectionGroup,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  getDocs,
  orderBy,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import type { AtlasManifest } from '@atlas-manifest/types';
import { auth, db, functions } from './client.js';
import type {
  Board,
  Sandbox,
  SandboxStatus,
  AnnotationThread,
  AnnotationMessage,
  PresenceViewer,
  ManifestDiff,
  EntityHistoryEntry,
  AskAtlasResult,
} from '../state/atlasApiTypes.js';
import type { InternalUser } from '../state/store.js';

const PROJECT_ID = import.meta.env.VITE_INTERNAL_FIREBASE_PROJECT_ID;
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';

if (!PROJECT_ID) {
  // Fail loud at startup — a blank project id silently builds a broken
  // `cloudfunctions.net//…` URL that 404s on every Ask Atlas request.
  throw new Error(
    'VITE_INTERNAL_FIREBASE_PROJECT_ID is not set — check internal/atlas/frontend/.env.local',
  );
}

const askAtlasUrl = USE_EMULATORS
  ? `http://127.0.0.1:5001/${PROJECT_ID}/us-central1/askAtlas`
  : `https://us-central1-${PROJECT_ID}.cloudfunctions.net/askAtlas`;

const toMillis = (ts: unknown): number => {
  if (ts && typeof (ts as { toMillis?: () => number }).toMillis === 'function') {
    return (ts as { toMillis: () => number }).toMillis();
  }
  return 0;
};

// ── Callables ───────────────────────────────────────────────────────────────

export async function callSaveBoard(name: string, entityIds: string[]): Promise<void> {
  // randomUUID is the path on every modern secure context; the fallback adds a
  // random suffix so two boards created in the same millisecond can't collide.
  const boardId =
    crypto.randomUUID?.() ?? `b-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  await httpsCallable(functions, 'saveBoard')({ boardId, name, entityIds });
}

export async function callDeleteBoard(boardId: string): Promise<void> {
  await httpsCallable(functions, 'deleteBoard')({ boardId });
}

export async function callSaveSandbox(
  sandboxId: string,
  name: string,
  ops: unknown[],
): Promise<void> {
  await httpsCallable(functions, 'saveSandbox')({ sandboxId, name, ops });
}

export async function callDeleteSandbox(sandboxId: string): Promise<void> {
  await httpsCallable(functions, 'deleteSandbox')({ sandboxId });
}

export async function callSubmitSandboxForReview(
  sandboxId: string,
): Promise<{ ok: true; sandboxId: string } | { ok: false; error: string }> {
  try {
    const result = await httpsCallable<
      { sandboxId: string },
      { ok: true; sandboxId: string } | { ok: false; error: string }
    >(functions, 'submitSandboxForReview')({ sandboxId });
    return result.data;
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Failed to submit for review',
    };
  }
}

export async function callPublishSandbox(
  sandboxId: string,
  ownerUid?: string,
): Promise<
  | { ok: true; sandboxId: string; prUrl: string; prNumber: number; publishedAt: number }
  | { ok: false; error: string }
> {
  try {
    const result = await httpsCallable<
      { sandboxId: string; ownerUid?: string },
      | { ok: true; sandboxId: string; prUrl: string; prNumber: number; publishedAt: number }
      | { ok: false; error: string }
    >(functions, 'publishSandbox')({ sandboxId, ...(ownerUid ? { ownerUid } : {}) });
    return result.data;
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Failed to publish sandbox' };
  }
}

export async function callSeedClickablePrototype(
  overlayManifest: AtlasManifest,
): Promise<
  | { ok: true; documentCount: number; emulatorUrl: string }
  | { ok: false; error: string }
> {
  try {
    const result = await httpsCallable<
      { overlayManifest: AtlasManifest },
      { documentCount: number; emulatorUrl: string }
    >(functions, 'seedClickablePrototype')({ overlayManifest });
    return { ok: true, ...result.data };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Failed to generate prototype.' };
  }
}

export async function callSaveAnnotation(
  entityId: string,
  threadId: string,
  body: string,
): Promise<void> {
  await httpsCallable(functions, 'saveAnnotation')({ entityId, threadId, body });
}

export async function callResolveAnnotation(
  entityId: string,
  threadId: string,
  resolved: boolean,
): Promise<void> {
  await httpsCallable(functions, 'resolveAnnotation')({ entityId, threadId, resolved });
}

export async function callDiffManifests(fromSha: string, toSha: string): Promise<ManifestDiff> {
  const result = await httpsCallable<unknown, ManifestDiff>(functions, 'diffManifests')({
    fromSha,
    toSha,
  });
  return result.data;
}

export async function callEntityHistory(entityId: string): Promise<EntityHistoryEntry[]> {
  const result = await httpsCallable<unknown, EntityHistoryEntry[]>(functions, 'entityHistory')({
    entityId,
  });
  return result.data;
}

// ── Firestore subscriptions ─────────────────────────────────────────────────

/** A listener failing (e.g. permission revoked) must surface, not vanish. */
const onWatchError =
  (label: string) =>
  (err: unknown): void => {
    console.error(`[atlas] ${label} subscription error:`, err);
  };

export function watchBoards(uid: string, onBoards: (boards: Board[]) => void): () => void {
  return onSnapshot(
    collection(db, `users/${uid}/boards`),
    (snap) => {
      const boards = snap.docs.map((d) => ({
        id: d.id,
        name: String(d.get('name') ?? d.id),
        entityIds: Array.isArray(d.get('entityIds')) ? (d.get('entityIds') as string[]) : [],
        updatedAt: toMillis(d.get('updatedAt')),
      }));
      boards.sort((a, b) => b.updatedAt - a.updatedAt);
      onBoards(boards);
    },
    onWatchError('watchBoards'),
  );
}

const VALID_SANDBOX_STATUSES = new Set(['draft', 'under_review', 'published']);

export function watchSandboxes(
  uid: string,
  onSandboxes: (sandboxes: Sandbox[]) => void,
): () => void {
  return onSnapshot(
    collection(db, `users/${uid}/sandboxes`),
    (snap) => {
      const list = snap.docs.map((d) => {
        const status = d.get('status');
        const aiConfidence = d.get('aiConfidence');
        const domainId = d.get('domainId');
        // publishedAt is a plain epoch-ms number (Date.now() server-side),
        // NOT a Firestore Timestamp like updatedAt/reviewSubmittedAt — toMillis()
        // would silently coerce it to 0, so it's read directly instead.
        const publishedAt = d.get('publishedAt');
        return {
          id: d.id,
          name: String(d.get('name') ?? d.id),
          ops: Array.isArray(d.get('ops')) ? (d.get('ops') as unknown[]) : [],
          updatedAt: toMillis(d.get('updatedAt')),
          ...(typeof status === 'string' && VALID_SANDBOX_STATUSES.has(status)
            ? { status: status as SandboxStatus }
            : {}),
          ...(typeof aiConfidence === 'number' ? { aiConfidence } : {}),
          ...(typeof domainId === 'string' ? { domainId } : {}),
          ...(typeof publishedAt === 'number' ? { publishedAt } : {}),
        };
      });
      list.sort((a, b) => b.updatedAt - a.updatedAt);
      onSandboxes(list);
    },
    onWatchError('watchSandboxes'),
  );
}

/**
 * Admin-only: watch every under-review sandbox across all users, via
 * collectionGroup — same cross-user query shape as functions/src/presence.ts's
 * db.collectionGroup('viewers'), applied client-side here. ownerUid comes from
 * the doc's own path (doc.ref.parent.parent!.id), not a duplicated field —
 * same approach the design spec settled on to avoid redundant storage.
 */
export function watchUnderReviewSandboxesForAdmin(
  onSandboxes: (sandboxes: Array<Sandbox & { ownerUid: string }>) => void,
): () => void {
  const q = query(collectionGroup(db, 'sandboxes'), where('status', '==', 'under_review'));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => {
      const ownerUid = d.ref.parent.parent!.id;
      return {
        id: d.id,
        ownerUid,
        name: String(d.get('name') ?? d.id),
        ops: Array.isArray(d.get('ops')) ? (d.get('ops') as unknown[]) : [],
        updatedAt: toMillis(d.get('updatedAt')),
        status: 'under_review' as const,
      };
    });
    list.sort((a, b) => b.updatedAt - a.updatedAt);
    onSandboxes(list);
  }, onWatchError('watchUnderReviewSandboxesForAdmin'));
}

export function watchAnnotations(
  entityId: string,
  onThreads: (threads: AnnotationThread[]) => void,
): () => void {
  // The callback is async (it fans out to each thread's messages), so snapshots
  // can overlap. A sequence guard ensures a slower earlier snapshot can never
  // clobber a newer one's result.
  let latestSeq = 0;
  return onSnapshot(
    collection(db, `annotations/${entityId}/threads`),
    async (snap) => {
      const mySeq = ++latestSeq;
      const threads = await Promise.all(
        snap.docs.map(async (threadDoc) => {
          const msgSnap = await getDocs(
            query(collection(threadDoc.ref, 'messages'), orderBy('createdAt', 'asc')),
          );
          const messages: AnnotationMessage[] = msgSnap.docs.map((m) => ({
            id: m.id,
            authorEmail: String(m.get('authorEmail') ?? 'unknown'),
            body: String(m.get('body') ?? ''),
            mentions: Array.isArray(m.get('mentions')) ? (m.get('mentions') as string[]) : [],
            createdAt: toMillis(m.get('createdAt')),
          }));
          return { id: threadDoc.id, resolved: threadDoc.get('resolved') === true, messages };
        }),
      );
      if (mySeq === latestSeq) onThreads(threads);
    },
    onWatchError('watchAnnotations'),
  );
}

export function watchPresence(
  entityId: string,
  onViewers: (viewers: PresenceViewer[]) => void,
): () => void {
  return onSnapshot(
    collection(db, `presence/${entityId}/viewers`),
    (snap) => {
      onViewers(
        snap.docs.map((d) => ({
          uid: d.id,
          displayName: String(d.get('displayName') ?? d.id),
          heartbeatAt: Number(d.get('heartbeatAt') ?? 0),
        })),
      );
    },
    onWatchError('watchPresence'),
  );
}

export async function writePresenceHeartbeat(
  entityId: string,
  user: InternalUser,
): Promise<void> {
  try {
    await setDoc(doc(db, `presence/${entityId}/viewers/${user.uid}`), {
      displayName: user.displayName,
      heartbeatAt: Date.now(),
      at: serverTimestamp(),
    });
  } catch {
    // Presence is best-effort — a write failure must not break the view.
  }
}

export async function deletePresenceDoc(entityId: string, uid: string): Promise<void> {
  try {
    await deleteDoc(doc(db, `presence/${entityId}/viewers/${uid}`));
  } catch {
    // Best-effort — if the tab is closing the promise may not resolve.
  }
}

// ── Ask Atlas SSE client ────────────────────────────────────────────────────

/** POST to askAtlas and accumulate the SSE token stream into a final answer. */
export async function streamAskAtlas(
  question: string,
  summary: { entityCount: number; domainCount: number; domainNames: string[] },
  knownEntityIds: string[],
): Promise<AskAtlasResult> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(askAtlasUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
    body: JSON.stringify({ question, summary, knownEntityIds }),
  });
  if (!res.ok || !res.body) {
    throw new Error('Ask Atlas is unavailable right now.');
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answer = '';
  let fabricated: string[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';
    for (const evt of events) {
      const eventLine = evt.match(/^event: (.+)$/m)?.[1];
      const dataLine = evt.match(/^data: (.+)$/m)?.[1];
      if (!dataLine) continue;
      if (eventLine === 'error') {
        throw new Error('Ask Atlas is unavailable right now.');
      }
      // A truncated/garbled chunk must not abort the whole stream — skip it.
      let parsed: unknown;
      try {
        parsed = JSON.parse(dataLine);
      } catch {
        continue;
      }
      if (eventLine === 'token' && typeof parsed === 'string') {
        answer += parsed;
      } else if (eventLine === 'grounding') {
        const g = (parsed as { fabricated?: unknown }).fabricated;
        if (Array.isArray(g)) fabricated = g as string[];
      }
    }
  }
  return { answer, fabricated };
}
/* c8 ignore stop */
